import React, { useState, useEffect, useCallback } from 'react';
import { FaUser, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import KanbanColumn from './KanbanColumn';
import useSessionState from '@/hooks/useSessionState';
import '@/styles/FunctionSwimlanes.scss';
import '@/styles/ActionItemsSwimlanes.scss';
import * as Sentry from '@sentry/react';

/**
 * FunctionSwimlanes Component
 * 
 * Renders action items in function-based swimlanes:
 * - First swimlane: Items directly assigned to the user (expanded by default)
 * - Subsequent swimlanes: Items assigned to each function the user belongs to (collapsed by default)
 * 
 * Each swimlane is collapsible. Collapse state is persisted in sessionStorage
 * so it survives in-app navigation.
 */
const FunctionSwimlanes = ({
    userID,
    apiFetch,
    statuses,
    activeStatuses,
    showClosedItems,
    closedItemsLimit,
    onLoadMoreClosed,
    propertiesMDA,
    navigate,
    needsMyAttention,
    getItemIcon,
    getItemDisplayText,
    getUserDisplayName,
    formatCardDate,
    getProjectName,
    allUsers,
    isAssignedToMyDepartment
}) => {
    const [functionData, setFunctionData] = useState(null);
    const [loading, setLoading] = useState(true);
    // Persist collapsed state across navigation; keys are swimlane identifiers
    const [collapsedMap, setCollapsedMap] = useSessionState('ai_fn_swimlane_collapsed', {});

    /**
     * Toggle a swimlane's collapsed state
     */
    const toggleCollapsed = useCallback((id) => {
        setCollapsedMap(prev => ({ ...prev, [id]: !prev[id] }));
    }, [setCollapsedMap]);

    /**
     * Determines if a swimlane is collapsed.
     * First swimlane ('direct') defaults to expanded; all others default to collapsed.
     */
    const isCollapsed = useCallback((id, isFirst) => {
        if (id in collapsedMap) return collapsedMap[id];
        // Default: first swimlane expanded, rest collapsed
        return !isFirst;
    }, [collapsedMap]);

    /**
     * Fetch function-based action items from the API
     */
    useEffect(() => {
        const fetchFunctionItems = async () => {
            try {
                setLoading(true);
                const response = await apiFetch(`/api/ActionItems/action-items/by-function/${userID}`);
                const data = await response.json();

                if (data.success) {
                    setFunctionData(data.data);
                }
            } catch (error) {
                Sentry.captureException(error);
                console.error('Error fetching function-based action items:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchFunctionItems();
    }, [userID, apiFetch]);

    if (loading) {
        return (
            <div className="function-swimlanes-loading">
                <div className="loading-spinner" />
                <p>Loading function-based view...</p>
            </div>
        );
    }

    if (!functionData) {
        return (
            <div className="function-swimlanes-error">
                <p>Unable to load function-based view. Please try again.</p>
            </div>
        );
    }

    /**
     * Groups items by status for a given array of items
     */
    const groupByStatus = (items) => {
        const grouped = {};
        statuses.forEach(status => {
            grouped[status] = items.filter(item => item.status === status);
        });
        return grouped;
    };

    /**
     * Renders a collapsed summary showing status counts as small badges
     */
    const renderCollapsedSummary = (items) => {
        if (!items || items.length === 0) return null;
        return (
            <div className="swimlane-collapsed-summary">
                {(showClosedItems ? statuses : activeStatuses).map(status => {
                    const count = items.filter(i => i.status === status).length;
                    if (count === 0) return null;
                    return (
                        <span key={status} className={`summary-badge status-${status.toLowerCase().replace('-', '')}`}>
                            {count}
                        </span>
                    );
                })}
            </div>
        );
    };

    // Group direct assignments by status
    const directItems = functionData.directAssignments || [];
    const directItemsByStatus = groupByStatus(directItems);
    const directCollapsed = isCollapsed('direct', true);

    return (
            <div className="swimlanes-container">
                {/* Sticky status labels row */}
                <div className="swimlanes-status-headers">
                    {(showClosedItems ? statuses : activeStatuses).map(status => (
                        <div
                            key={status}
                            className={`swimlane-status-label status-${status.replace(/\s+/g, '').replace(/-/g, '').toLowerCase()}`}
                        >
                            {status}
                        </div>
                    ))}
                </div>

                <div className="function-swimlanes">
                {/* First Swimlane: Direct Assignments */}
                <div className={`swimlane ${directCollapsed ? 'collapsed' : ''}`}>
                    <div
                        className="swimlane-header clickable"
                        style={{ borderLeftColor: '#3498db' }}
                        onClick={() => toggleCollapsed('direct')}
                    >
                        <button
                            className="swimlane-toggle-btn"
                            onClick={(e) => { e.stopPropagation(); toggleCollapsed('direct'); }}
                            aria-label={directCollapsed ? 'Expand swimlane' : 'Collapse swimlane'}
                        >
                            {directCollapsed ? <FaChevronDown /> : <FaChevronUp />}
                        </button>
                        <div className="swimlane-indicator" style={{ backgroundColor: '#3498db' }} />
                        <div className="swimlane-label">
                            <FaUser />
                            <span>My Direct Assignments</span>
                            <span className="swimlane-count">
                                ({directItems.length})
                            </span>
                        </div>
                        {directCollapsed && renderCollapsedSummary(directItems)}
                    </div>
                    {!directCollapsed && (
                        <div className="swimlane-body">
                            {(showClosedItems ? statuses : activeStatuses).map(status => {
                                const columnItems = status === "Closed" && showClosedItems
                                    ? directItemsByStatus[status].slice(0, closedItemsLimit)
                                    : directItemsByStatus[status];
                                const totalClosedItems = status === "Closed" ? directItemsByStatus[status].length : 0;
                                const hasMoreClosedItems = status === "Closed" && totalClosedItems > closedItemsLimit;

                                return (
                                    <KanbanColumn
                                        key={`direct-${status}`}
                                        status={status}
                                        droppableId={`direct-${status}`}
                                        items={columnItems}
                                        propertiesMDA={propertiesMDA}
                                        navigate={navigate}
                                        hasMore={hasMoreClosedItems}
                                        totalItems={totalClosedItems}
                                        onLoadMore={onLoadMoreClosed}
                                        needsMyAttention={needsMyAttention}
                                        getItemIcon={getItemIcon}
                                        getItemDisplayText={getItemDisplayText}
                                        getUserDisplayName={getUserDisplayName}
                                        formatCardDate={formatCardDate}
                                        getProjectName={getProjectName}
                                        allUsers={allUsers}
                                        isAssignedToMyDepartment={isAssignedToMyDepartment}
                                        hideHeader={true}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Function Swimlanes */}
                {functionData.functionSwimlanes?.map((functionSwimlane) => {
                    const functionItemsByStatus = groupByStatus(functionSwimlane.items || []);
                    const swimlaneId = `fn-${functionSwimlane.functionId}`;
                    const fnCollapsed = isCollapsed(swimlaneId, false);
                    const fnItems = functionSwimlane.items || [];

                    return (
                        <div key={functionSwimlane.functionId} className={`swimlane ${fnCollapsed ? 'collapsed' : ''}`}>
                            <div 
                                className="swimlane-header clickable" 
                                style={{ borderLeftColor: functionSwimlane.color || '#f39c12' }}
                                onClick={() => toggleCollapsed(swimlaneId)}
                            >
                                <button
                                    className="swimlane-toggle-btn"
                                    onClick={(e) => { e.stopPropagation(); toggleCollapsed(swimlaneId); }}
                                    aria-label={fnCollapsed ? 'Expand swimlane' : 'Collapse swimlane'}
                                >
                                    {fnCollapsed ? <FaChevronDown /> : <FaChevronUp />}
                                </button>
                                <div
                                    className="swimlane-indicator"
                                    style={{ backgroundColor: functionSwimlane.color || '#f39c12' }}
                                />
                                <div className="swimlane-label">
                                    {functionSwimlane.iconClass && (
                                        <i className={`fas ${functionSwimlane.iconClass}`}></i>
                                    )}
                                    <span>{functionSwimlane.functionName}</span>
                                    <span className="swimlane-count">
                                        ({fnItems.length})
                                    </span>
                                </div>
                                {fnCollapsed && renderCollapsedSummary(fnItems)}
                                {functionSwimlane.description && !fnCollapsed && (
                                    <div className="swimlane-description">{functionSwimlane.description}</div>
                                )}
                            </div>
                            {!fnCollapsed && (
                                <div className="swimlane-body">
                                    {(showClosedItems ? statuses : activeStatuses).map(status => {
                                        const columnItems = status === "Closed" && showClosedItems
                                            ? functionItemsByStatus[status].slice(0, closedItemsLimit)
                                            : functionItemsByStatus[status];
                                        const totalClosedItems = status === "Closed" ? functionItemsByStatus[status].length : 0;
                                        const hasMoreClosedItems = status === "Closed" && totalClosedItems > closedItemsLimit;

                                        return (
                                            <KanbanColumn
                                                key={`function-${functionSwimlane.functionId}-${status}`}
                                                status={status}
                                                droppableId={`function-${functionSwimlane.functionId}-${status}`}
                                                items={columnItems}
                                                propertiesMDA={propertiesMDA}
                                                navigate={navigate}
                                                hasMore={hasMoreClosedItems}
                                                totalItems={totalClosedItems}
                                                onLoadMore={onLoadMoreClosed}
                                                needsMyAttention={needsMyAttention}
                                                getItemIcon={getItemIcon}
                                                getItemDisplayText={getItemDisplayText}
                                                getUserDisplayName={getUserDisplayName}
                                                formatCardDate={formatCardDate}
                                                getProjectName={getProjectName}
                                                allUsers={allUsers}
                                                isAssignedToMyDepartment={isAssignedToMyDepartment}
                                                hideHeader={true}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Empty state if no functions */}
                {(!functionData.functionSwimlanes || functionData.functionSwimlanes.length === 0) && (
                    <div className="swimlane-empty-state">
                        <p>You are not assigned to any functions yet.</p>
                        <p>Contact your administrator to be assigned to functions like Debtors, Creditors, or IT Support.</p>
                    </div>
                )}
                </div>
            </div>
    );
};

export default FunctionSwimlanes;
